import { CodeEditor } from '@acrodata/code-editor';
import { ChangeDetectionStrategy,Component, computed, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { languages } from '@codemirror/language-data';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { ConfirmDialogService } from 'app/shared/dialog/confirm-dialog-service';

import { DesignerCustomizationsService } from './designer-customizations.service';

@Component({
    selector: 'admin-store-designer-custom-javascript',
    templateUrl: './store-designer-custom-javascript.component.html',
    styleUrls: ['./store-designer-custom-javascript.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, MatButton, SaveButtonComponent, ErrorComponent, FormsModule, MatIcon, CodeEditor]
})
export class StoreDesignerCustomJavascriptComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly designerCustomizationsService = inject(DesignerCustomizationsService);
    private readonly confirmDialogService = inject(ConfirmDialogService);

    savedJavascript: string;
    javascript: string;
    storeId: string;
    languages = languages

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.subscribeWithGenericLoadingErrorHandling(this.designerCustomizationsService.getStoreFiles(this.storeId), (storeFiles) => {
                if (!storeFiles.javascript?.url) {
                    this.javascript = this.savedJavascript = '';
                    this.pageStatus = PageStatus.loaded;
                } else {
                    this.designerCustomizationsService
                        .getCustomizationFile(storeFiles.javascript?.url)
                        .subscribe({
                            next: (javascript) => {
                                this.javascript = this.savedJavascript = javascript;
                                this.pageStatus = PageStatus.loaded;
                            },
                            error: (err) => {
                                this.pageError = err;
                                this.pageStatus = PageStatus.loadError;
                            }
                        });
                }
            });
        }));
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    public get canSave(): Signal<boolean> {
        return computed(() => this.savedJavascript !== this.javascript);
    }

    save(): void {
        this.subscribeWithGenericSavingErrorHandling(this.designerCustomizationsService.updateJavascriptFile({
            storeId: this.storeId,
            content: this.javascript
        }), () => {
            this.savedJavascript = this.javascript;
        });
    }

    public get canDelete(): Signal<boolean> {
        return computed(() => this.pageStatus === PageStatus.loaded && this.savedJavascript !== '');
    }

    delete(): void {
        this.confirmDialogService
            .confirm(
                'Confirmation',
                `Do you want to delete the custom JavaScript for this store?`
            ).subscribe(() => {
                this.subscribeWithGenericSavingErrorHandling(this.designerCustomizationsService.deleteJavascriptFile({
                    storeId: this.storeId
                }), () => {
                    this.javascript = this.savedJavascript = '';
                });
            }
        );
    }

    async onFilesChanged(files: FileList) {
        if (!files.length) {
            return;
        }
        this.javascript = await files[0].text();
    }

    handleSaveEditor(event: KeyboardEvent) {
        if (event.key === 's' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault(); // Prevent browser's save dialog
            this.save();
        }
    }
}
